import { DEFAULT_TYPE_ICON, TYPE_ICON_MAX_LENGTH, TYPE_ICON_PRESETS } from '@edms/shared';
import { useRef, useState, type ChangeEvent } from 'react';

const isImage = (icon: string) => icon.startsWith('data:image/');

/** Иконка типа заявки: эмодзи или своя картинка. */
export function TypeIcon({ icon, className = 'type-icon' }: { icon: string | null | undefined; className?: string }) {
  const value = icon || DEFAULT_TYPE_ICON;
  return (
    <span className={className} aria-hidden>
      {isImage(value) ? <img src={value} alt="" /> : value}
    </span>
  );
}

const SIZE = 96;

/** Картинку уменьшаем до 96×96 PNG: в базе хранится несколько килобайт, а не исходный файл. */
async function toIconDataUrl(file: File): Promise<string> {
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = () => reject(new Error('Не удалось прочитать картинку'));
      i.src = url;
    });
    const canvas = document.createElement('canvas');
    canvas.width = SIZE;
    canvas.height = SIZE;
    const ctx = canvas.getContext('2d')!;
    // Вписываем целиком, по центру, сохраняя пропорции.
    const scale = Math.min(SIZE / img.naturalWidth, SIZE / img.naturalHeight);
    const w = img.naturalWidth * scale;
    const h = img.naturalHeight * scale;
    ctx.drawImage(img, (SIZE - w) / 2, (SIZE - h) / 2, w, h);
    let data = canvas.toDataURL('image/png');
    if (data.length > TYPE_ICON_MAX_LENGTH) data = canvas.toDataURL('image/webp', 0.85);
    if (data.length > TYPE_ICON_MAX_LENGTH) throw new Error('Картинка слишком сложная — выберите попроще');
    return data;
  } finally {
    URL.revokeObjectURL(url);
  }
}

export function IconPicker({ value, onChange }: { value: string | null; onChange: (icon: string | null) => void }) {
  const file = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const custom = value && !isImage(value) && !(TYPE_ICON_PRESETS as readonly string[]).includes(value) ? value : '';

  const upload = async (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    setError(null);
    try {
      onChange(await toIconDataUrl(f));
    } catch (err) {
      setError((err as Error).message);
    }
  };

  return (
    <div className="icon-picker">
      <div className="icon-picker-head">
        <TypeIcon icon={value} className="type-icon icon-preview" />
        <div className="icon-picker-own">
          <input
            className="input"
            value={custom}
            maxLength={16}
            placeholder="Своя: вставьте эмодзи"
            aria-label="Своя иконка"
            onChange={(e) => onChange(e.target.value.trim() || null)}
          />
          <button type="button" className="btn btn-sm" onClick={() => file.current?.click()}>
            Загрузить картинку
          </button>
          {value && (
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => onChange(null)}>
              Сбросить
            </button>
          )}
          <input ref={file} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml,image/gif" hidden onChange={(e) => void upload(e)} />
        </div>
      </div>
      {error && <span className="field-error">{error}</span>}
      <div className="icon-grid" role="listbox" aria-label="Готовые иконки">
        {TYPE_ICON_PRESETS.map((icon) => (
          <button
            key={icon}
            type="button"
            role="option"
            aria-selected={value === icon}
            className={`icon-option${value === icon ? ' selected' : ''}`}
            onClick={() => onChange(icon)}
          >
            {icon}
          </button>
        ))}
      </div>
    </div>
  );
}
